import {
  ChangeDetectorRef,
  Component,
  EnvironmentInjector,
  inject,
  Injector,
  Input,
  Type,
} from '@angular/core';
import { IconColourEnum } from '../../../../shared-modules/shared-icons/models/icons-colour.enum';
import { IconsEnum } from '../../../../shared-modules/shared-icons/models/icons.enum';
import {
  ISharedModalBasic,
  ISharedModalBasicResponse,
} from '../../../shared-models/modals/shared-modal-basic';
import { ModalControllerService } from '../../../shared-services/overlay/modal-controller.service';
import { PopoverControllerService } from '../../../shared-services/overlay/popover-controller.service';

@Component({
  selector: 'app-shared-modal-basic',
  templateUrl: './shared-modal-basic.component.html',
  styleUrls: ['./shared-modal-basic.component.scss'],
  standalone: false,
})
export class SharedModalBasicComponent implements ISharedModalBasic {
  icons: typeof IconsEnum = IconsEnum;
  protected readonly iconColourEnum = IconColourEnum;

  @Input() buttonAccept: boolean;
  @Input() buttonAcceptText: string;
  @Input() buttonClose: boolean;
  @Input() buttonCloseCross: boolean;
  @Input() buttonCloseText: string;
  @Input() buttonSecondary: boolean;
  @Input() buttonSecondaryText: string;
  @Input() buttonSecondaryClosesModal = true;
  /** Invoked when the secondary button is pressed and `buttonSecondaryClosesModal` is false. */
  @Input() secondaryAction?: () => void | Promise<void>;
  @Input() modalTitle: string;
  @Input() modalTitleIcon: IconsEnum;
  @Input() modalTitleIconColor: IconColourEnum;
  @Input() contextHeading: string;
  @Input() contextDescription: string[];
  @Input() isImageVisible: boolean;
  @Input() imageUrl: string;
  @Input() modalBodyScrollable: boolean = true;

  customContentInjector?: Injector;
  #customContentComponent?: Type<unknown>;
  #customContentData?: Record<string, unknown>;

  constructor(
    private modalController: ModalControllerService,
    private readonly cdr: ChangeDetectorRef,
    private readonly envInjector: EnvironmentInjector = inject(EnvironmentInjector),
  ) {}

  get customContentComponent(): Type<unknown> | undefined {
    return this.#customContentComponent;
  }

  @Input() set customContentComponent(value: Type<unknown> | undefined) {
    this.#customContentComponent = value;
    this.tryInitInjector();
  }

  @Input() set customContentData(value: Record<string, unknown> | undefined) {
    this.#customContentData = value;
    this.tryInitInjector();
  }

  @Input() set popoverReference(popover: PopoverControllerService | undefined) {
    void popover?.dismiss();
  }

  onButtonPress(btn: 'button-accept' | 'button-secondary' | 'button-close'): void {
    let role: string;
    if (btn === 'button-accept') {
      role = 'modal-accept';
    } else if (btn === 'button-secondary') {
      role = 'modal-secondary';
    } else {
      role = 'modal-close';
    }
    void this.modalController
      .dismiss({ buttonPressed: btn } as ISharedModalBasicResponse, role)
      .then(() => true);
  }

  async onSecondaryButtonClick(): Promise<void> {
    if (this.buttonSecondaryClosesModal === false) {
      await Promise.resolve(this.secondaryAction?.());
      return;
    }
    this.onButtonPress('button-secondary');
  }

  /**
   * Escapes HTML then converts `**text**` segments to `<strong>` for modal copy.
   */
  formatContextText(text: string): string {
    const escaped = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    return escaped.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  }

  private tryInitInjector(): void {
    if (!this.#customContentComponent || this.#customContentData == null) {
      return;
    }

    this.customContentInjector = Injector.create({
      providers: [
        {
          provide: 'SHARED_MODAL_CUSTOM_DATA',
          useValue: this.#customContentData,
        },
      ],
      parent: this.envInjector,
    });

    this.cdr.detectChanges();
  }
}
